// React
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// UI Components
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { NoResults } from "ui/components/NoResults";
import { Modal } from "ui/components/Modal";
import ModalBody from "ui/components/Modal/ModalBody";
import ModalHeader from "ui/components/Modal/ModalHeader";
import TabMenu from "../TabMenu/TabMenu";
import VehicleGroupEditCard from "../VehicleGroupEditCard/VehicleGroupEditCard";

// Services & Helpers
import { transformRoute, useTranslate } from "helpers";
import { getVehicleGroupsAPI } from "services/api/calculations/vehiclegroup/api";
import { Pagination } from "services/search";
import { DataWithPagination } from "services/api";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Local Constants
import { initialDataWithPagination } from "services/api/consts";
import { Routes } from "ui/modules/calculation/navigation";
import {
  CALCULATION_VEHICLE_GROUP_CREATE_PAGE_TITLE,
  CALCULATION_VEHICLE_GROUP_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Types
import {
  VehicleGroupRow,
  VehicleGroupSearchFormValues,
} from "services/api/calculations/vehiclegroup/types";
import {
  VehicleGroupSearchPageCmp,
  VehicleGroupSearchPageProps,
} from "./types";

// Components
import VehicleGroupSearchFilters from "./components/VehicleGroupSearchFilters/VehicleGroupSearchFilters";
import VehicleGroupSearchResults from "./components/VehicleGroupSearchResults/VehicleGroupSearchResults";

const VehicleGroupSearchPage: VehicleGroupSearchPageCmp = (
  props: VehicleGroupSearchPageProps
) => {
  const { t } = useTranslate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [vehicleGroupId, setVehicleGroupId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getVehicleGroupsAPI(pagination, name);

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(Routes.CalculationVehicleGroupDetailPageRouter, {
        vehicleGroupId: id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setVehicleGroupId("");
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: VehicleGroupSearchFormValues) => {
      fetchSearchResult(searchResults.pagination, searchFormValues.search);
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setVehicleGroupId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md">
          <PageHeader>
            <PageTitle
              title={CALCULATION_VEHICLE_GROUP_SEARCH_PAGE_TITLE.text}
              icon={CALCULATION_VEHICLE_GROUP_SEARCH_PAGE_TITLE.icon}
              iconColor={CALCULATION_VEHICLE_GROUP_SEARCH_PAGE_TITLE.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
          <TabMenu />
        </div>
        <div className="px-0 pt-2 mx-[-1px] xl:px-6">
          <VehicleGroupSearchFilters
            onSearchChange={handleSearchChange}
            onAddNew={handleAddNew}
          />
          {!isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults
              message={t(
                "admin.calculation.setting.vehiclegroup.search.noresults"
              )}
            />
          ) : (
            <VehicleGroupSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onRowClick={handleRowClick}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
            />
          )}
        </div>
      </div>
      {showAddNewModal && (
        <Modal show={showAddNewModal} onHide={() => setShowAddNewModal(false)}>
          <ModalHeader
            title={CALCULATION_VEHICLE_GROUP_CREATE_PAGE_TITLE.text}
            onHide={() => setShowAddNewModal(false)}
          />
          <ModalBody>
            <VehicleGroupEditCard
              vehicleGroupId={vehicleGroupId}
              onSuccess={handleEditSuccess}
            />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

VehicleGroupSearchPage.route = Routes.CalculationVehicleGroupSearchPageRouter;

export default VehicleGroupSearchPage;
